export default class TimeUtils
{
	/**
	 * converts the dateTimeString received from the API server into something more readable
	 * @static
	 * @param {String} dateTimeString a date-time string in the following format - YYYY-MM-DDTHH:mm:ss.xxxZ
	 * @returns a formatted date-time string with the following format - YYYY-MM-DD HH:mm
	 * @memberof TimeUtils
	 */
	static getDateFromString(dateTimeString: string): string
	{
		if (typeof (dateTimeString) !== "string")
		{return dateTimeString;}

		const dateTimeSections = dateTimeString.split('T');
		if (dateTimeSections.length < 2)
		{return dateTimeString;}

		const date = dateTimeSections[0];
		const time = dateTimeSections[1].substring(0, dateTimeSections[1].lastIndexOf(':'));

		return date + " " + time;
	}

	/**
	 * converts the UTC(GMT) timezone dateTimeString received from the API server into something more readable
	 * @static
	 * @param {String} dateTimeString a date-time string in the following format - YYYY-MM-DDTHH:mm:ss.xxxZ
	 * @returns a localized date format string
	 * @memberof TimeUtils
	 */
	static getDateFromGMTString(dateTimeString: string)
	{
		if (typeof (dateTimeString) !== "string")
		{return dateTimeString;}

		// using https://stackoverflow.com/a/9893752/618726 we determine that "af-za" matches ISO-8601 format
		return new Date(dateTimeString).toLocaleString("af-za");
	}

	/**
	 * @static
	 * @param {String} mongoDbDate a date in the format mongo sends.
	 * @returns {String} formatted date and time pleasent for the eye.
	 * @memberof TimeUtils
	 */
	static formatDate(mongoDbDate: string): string
	{
		if (!mongoDbDate) return "";
		const dateObj = new Date(mongoDbDate);

		const day = String(dateObj.getDate());
		const month = String(dateObj.getMonth() + 1);
		const year = String(dateObj.getFullYear());
		let hour = String(dateObj.getHours());
		let minute = String(dateObj.getMinutes());

		hour = hour.length === 1 ? '0' + hour : hour;
		minute = minute.length === 1 ? '0' + minute : minute;

		return month + "/" + day + "/" + year + "  " + hour + ":" + minute;
	}

	/**
	 * converts a given number of seconds into a period-based clock-like format
	 * @static
	 * @param {Number} seconds the total number of seconds to convert
	 * @returns returns an array of periods sorted from largest to smallest, each period is a string with a leading zero if needed.
	 * 
	 * the size of the array indicates the number of periods, where 1 is seconds only, and the maximum of 6 is "years, months, days, hours, minutes, seconds"
	 * @memberof TimeUtils
	 */
	static secondsToPeriods(seconds: number): string[]
	{
		const periods = [
			31536000, // year
			2592000, // month
			86400, // day
			3600, // hour
			60, // minute
		];

		let total = seconds;
		const output = new Array<string>();

		for (const period of periods)
		{
			if (total > period)
			{
				const periodCount = Math.floor(total / period);
				total -= periodCount * period;

				output.push(periodCount.toString().padStart(2, "0"));
			}
		}

		output.push(Math.trunc(total).toString().padStart(2, "0"));
		return output;
	}

	/**
	 * **Intended for use with TimeUtils.secondsToPeriods() function**  
	 * This function takes an array returned by the secondsToPeriods function and returns an array of friendly period names  
	 * Remember that periods are sorted from largest to smallest, so the first period in the array is the largest one  
	 * For example, giving an array of 3 periods, will return an array of ["hour", "minute", "second"]
	 *
	 * @static
	 * @param {string[]} periods
	 * @return {*} 
	 * @memberof TimeUtils
	 */
	static getFriendlyPeriodNames(periods: string[])
	{
		const friendlyPeriodNames = [
			"year",
			"month",
			"day",
			"hour",
			"minute",
			"second",
		];

		const output = new Array<string>();
		for(let i = 0; i < Math.min(periods.length, friendlyPeriodNames.length); i++)
		{
			output.unshift(friendlyPeriodNames[friendlyPeriodNames.length - i - 1]);
		}

		return output;
	}

	/**
	 * Returns a user-friendly formatted string representing the difference between 2 given times, up to the first given periodCount periods
	 *
	 * @static
	 * @param {number | string | Date} startTime
	 * @param {number | string | Date} [endTime=Date.now()]
	 * @param {number} [periodCount=2]
	 * @return {*} 
	 * @memberof TimeUtils
	 */
	static getFriendlyTimeSince(startTime?: number | string | Date, endTime: number | string | Date = Date.now(), periodCount = 2)
	{
		if (!endTime)
		{
			endTime = Date.now();
		}

		if(!startTime)
		{
			return "N/A";
		}
		else
		{
			const timeDifferenceSeconds = (new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000;
			if(timeDifferenceSeconds < 0)
			{
				return "N/A";
			}

			const periodDifference = TimeUtils.secondsToPeriods(timeDifferenceSeconds);
			const friendlyPeriods = TimeUtils.getFriendlyPeriodNames(periodDifference);

			// display the time difference in a friendly format, up to 2 periods (e.g. 2h 10m, 45s, etc.)
			let output = "";
			for(let i = 0; i < Math.min(friendlyPeriods.length, periodCount); i++)
			{
				// we only the first letter of the period name, so 'h' instead of 'hour'
				// we also strip leading 0s from the period value, so e.g. 01h becomes 1h
				output += `${periodDifference[i].replace(/^0+/, "")}${friendlyPeriods[i][0]} `; 
			}

			if(!output.trim().length)
			{
				output += "0s";
			}

			return output.trim() + " ago";
		}
	}

	/**
	 * a simple async function that waits for some time in case another async function needs to delay execution for an arbitrary amounf of time instead of condition
	 * - taken from SO: https://stackoverflow.com/a/39914235/618726
	 * @static
	 * @param {Number} milliseconds time to wait for in milliseconds
	 * @returns an empty promise that waits for the given milliseconds before resolving
	 * @memberof TimeUtils
	 */
	static waitForTime(milliseconds: number)
	{
		return new Promise((resolve) => setTimeout(resolve, milliseconds));
	}

	/**
	 * - based on: https://stackoverflow.com/questions/3177836/how-to-format-time-since-xxx-e-g-4-minutes-ago-similar-to-stack-exchange-site
	 * @static
	 * @param {string | number | Date} date
	 * @returns {String} friendly description of how many time units have passed since the given date, stated by the biggest time unit that at least one of which has passed since the given date.
	 * @memberof TimeUtils
	 */
	 static timeSince(date: string | number | Date): string
	 {
		const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);

		function getIntervalDescription(intervalName: string, intervalCount: number): string
		{
			return intervalCount + " " + intervalName + (intervalCount > 1 ? "s" : "") + " ago";
		}

		let interval = seconds / 31536000;

		if (interval > 1)
		{
			return getIntervalDescription("year", Math.floor(interval));
		}

		interval = seconds / 2592000;
		if (interval > 1)
		{
			return getIntervalDescription("month", Math.floor(interval));
		}
		interval = seconds / 86400;
		if (interval > 1)
		{
			return getIntervalDescription("day", Math.floor(interval));
		}
		interval = seconds / 3600;
		if (interval > 1)
		{
			return getIntervalDescription("hour", Math.floor(interval));
		}
		interval = seconds / 60;
		if (interval > 1)
		{
			return getIntervalDescription("minute", Math.floor(interval));
		}

		return getIntervalDescription("second", Math.floor(interval));
	}

	static formatAMPM(date: Date)
	{
		//https://stackoverflow.com/questions/8888491/how-do-you-display-javascript-datetime-in-12-hour-am-pm-format
		let hours = date.getHours();
		const minutes = date.getMinutes();
		const ampm = hours >= 12 ? 'pm' : 'am';
		hours = hours % 12;
		hours = hours ? hours : 12; // the hour '0' should be '12'
		const minutesString = minutes < 10 ? '0'+minutes : minutes;
		const strTime = hours + ':' + minutesString + ' ' + ampm;
		return strTime;
	}
}
